import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Input, OnChanges, SimpleChanges, TemplateRef, inject } from '@angular/core';
import { Router } from '@angular/router';

import { LeagueModel } from '@frontend/sports/betting-offer/feature/model';
import { IntervalFilter } from '@frontend/sports/calendar/data-access';
import { CalendarService } from '@frontend/sports/calendar/feature';
import { EpcotConfig, FavouritesConfig, GridConfig, PrettyUrlsConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { CountItem, ItemType, LeagueItem, children } from '@frontend/sports/common/core/data-access/sport-model';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { CardThemeSurface, SportsThemedCardHeaderDirective } from '@frontend/sports/common/ui/sports-themed-card';
import { ThemeNotificationVariant } from '@frontend/sports/common/ui/sports-themed-notifcation-bubble';
import { SelectedTabItem, TabGroupWithCounterComponent, TabItemUiViewModel } from '@frontend/sports/common/ui/sports-themed-tab-group';
import { CompetitionRouteService } from '@frontend/sports/competition-list/feature';
import { getItemTypeForSport } from '@frontend/sports/competition-list/feature/models';
import { CompetitionLogoComponent } from '@frontend/sports/competition-logos/feature';
import { FavouriteLeagueToggleComponent } from '@frontend/sports/favourites/feature/league-toggle';
import { CompetitionRoute } from '@frontend/sports/navigation/core/feature/models';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { PrimaryNavigationDisplayType } from '@frontend/sports/types/configuration';
import { TabBarItem } from '@frontend/sports/web/tab-bar-utils';
import { WidgetPillComponent } from '@frontend/sports/widget/feature/pills';
import { WidgetTabBarDisplay, WidgetTabBarItem } from '@frontend/sports/widget/feature/tab-bar';
import { DsCardHeader } from '@frontend/ui/card-header';
import { lowerCase } from 'lodash-es';

import { FixtureTab } from '../competition-list/competition.models';
import { RewardTokenEligibleTagComponent } from './reward-token-eligible-tag/reward-token-eligible-tag.component';

@Component({
    selector: 'ms-fixture-list-header',
    templateUrl: './fixture-list-header.component.html',
    imports: [
        NgTemplateOutlet,
        FavouriteLeagueToggleComponent,
        CompetitionLogoComponent,
        RewardTokenEligibleTagComponent,
        NgClass,
        TabGroupWithCounterComponent,
        WidgetPillComponent,
        DsCardHeader,
        SportsThemedCardHeaderDirective,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FixtureListHeaderComponent implements OnChanges {
    sitecore = inject(Sitecore);
    private gridConfig = inject(GridConfig);
    private route = inject(CompetitionRouteService);
    private router = inject(Router);
    private urlConfig = inject(PrettyUrlsConfig);
    private calendar = inject(CalendarService);
    private trackingService = inject(TrackingService);
    private favouritesConfig = inject(FavouritesConfig);

    @Input() sport?: CountItem;
    @Input() navigation?: TabBarItem[];
    @Input() showFavoriteToggle? = true;
    @Input() competitionTitle: string;
    @Input() headerTitle = '';
    @Input() offerBadgeTemplate?: TemplateRef<any>;
    epcotEnabled = inject(EpcotConfig).isEnabled;

    @HostBinding('class') className = 'fixture-list-header';
    tabs: TabItemUiViewModel[] = [];
    pills: WidgetTabBarItem[] = [];

    @HostBinding('class.active') get active(): boolean {
        return !!this.title || (!!this.navigation && this.navigation.length > 0);
    }

    @HostBinding('class.calendar') isCalendarPage = false;

    title?: string;
    league?: LeagueModel;
    region?: CountItem;
    leagueIds?: number[];
    regionType: ItemType.Region | ItemType.Tournament;
    variant: ThemeNotificationVariant = {
        'mgm-dice': 'utility',
    };

    private showPrimaryNavAsPill = false;
    isFavouritesEnabled = this.favouritesConfig.isFavouritesEnabled;

    constructor() {
        this.showPrimaryNavAsPill = this.gridConfig.primaryNavigationType === PrimaryNavigationDisplayType.Pill;
    }

    themeHeaderSurface: CardThemeSurface = {
        'mgm-dice': 'low',
        'sports-interaction': 'highest',
        'sportingbet-br': 'low',
        'betboo-br': 'low',
    };

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.sport) {
            this.league = undefined;
            this.leagueIds = undefined;
            this.title = undefined;

            const route = this.route.params();
            const interval = this.calendar.getInterval(route.context);

            if (this.isInvalidContext(route, interval) || !changes.sport?.currentValue || !this.sport) {
                return;
            }

            if (interval) {
                this.title = this.headerTitle || interval.title;
                this.isCalendarPage = true;

                this.updateNavigationTabs();

                return;
            }

            this.isCalendarPage = false;

            this.regionType = getItemTypeForSport(this.sport.id);
            this.region = children(this.sport, this.regionType).find((item) => item.id === route.region);

            if (!route.league) {
                this.handleNoLeagueRoute();
            } else {
                this.handleLeagueRoute(route);
            }
        }
        this.updateNavigationTabs();
    }

    changedPill(selectedTab: WidgetTabBarItem): void {
        const tabItem = this.navigation?.find((t) => t.id.toString() === selectedTab.id);
        this.routeTab(tabItem);
    }

    changed(selectedTab: SelectedTabItem): void {
        const tabItem = this.navigation?.find((t) => t.id.toString() === selectedTab.name);
        this.routeTab(tabItem);
    }

    routeTab(tabItem: TabBarItem<number> | undefined) {
        if (!tabItem) {
            return;
        }
        const url = this.route.path();

        this.router
            .navigate([url], {
                replaceUrl: true,
                queryParams: {
                    tab: lowerCase(FixtureTab[tabItem.id]),
                },
            })
            .then(() => {
                const route = this.route.params();
                this.trackingService.track(trackingConstants.EVENT_NAVIGATION_MENUS, {
                    [trackingConstants.PAGE_NAVIGATION_MENUS]: `CompetitionPage_${route.marketOffer}_${tabItem.id}`,
                });
            });
    }

    get isTournament(): boolean {
        return this.regionType === ItemType.Tournament;
    }

    private isInvalidContext(route: CompetitionRoute, interval: IntervalFilter | undefined): boolean {
        return (
            route.context !== this.urlConfig.translations.betting &&
            route.context !== this.urlConfig.translations.standings &&
            route.context !== this.urlConfig.translations.bets &&
            route.context !== this.urlConfig.translations.conferences &&
            !interval
        );
    }

    private handleNoLeagueRoute(): void {
        if (!this.region) {
            return;
        }
        this.title = `${this.sitecore.globalMessages.All} ${this.region.name}`;
    }

    private handleLeagueRoute(route: CompetitionRoute): void {
        if (!this.sport) {
            return;
        }

        const league = children(this.sport, ItemType.Competition).pop() as LeagueItem;

        this.league = {
            id: league.realCompetitionId || league.id,
            name: league.name,
            parentLeagueId: league.siblings[0],
            imageProfile: league.imageProfile,
        };

        this.leagueIds = [this.league.id];

        if (this.league.parentLeagueId) {
            this.leagueIds.push(this.league.parentLeagueId);
        }

        if (route.conference && !this.competitionTitle) {
            const conference = children(this.sport, ItemType.Conference).pop();
            this.title = interpolateDoubleBracesString(this.sitecore.seo.ConferenceHeadingTitle, {
                conference: conference?.name ?? '',
            });

            return;
        }

        this.title =
            this.competitionTitle ||
            interpolateDoubleBracesString(this.sitecore.seo.LeagueHeadingTitle, {
                league: league.name,
            });
    }

    private updateNavigationTabs(): void {
        if (this.showPrimaryNavAsPill) {
            this.pills =
                this.navigation?.map((t) => ({
                    id: t.id.toString(),
                    icon: t.icon,
                    title: t.title,
                    active: t.active,
                    url: t.url,
                    display: WidgetTabBarDisplay.Default,
                    context: {},
                    itemId: t.id,
                    widgetId: '',
                })) || [];
        } else {
            this.tabs =
                this.navigation?.map((t) => ({
                    counter: t.count?.toString(),
                    id: t.id.toString(),
                    icon: t.icon,
                    title: t.title,
                    active: t.active,
                    url: t.url,
                })) || [];
        }
    }
}
